import { Sync, WarningAmberRounded } from "@mui/icons-material";
import {
  Box,
  Link,
  List,
  ListItem,
  Modal,
  Tooltip,
  Typography,
} from "@mui/material";
import { AxiosError } from "axios";
import { AvatarCandidate } from "components/avatar.candidate";
import { Button } from "components/buttons/button";
import { TabsContextProvider } from "components/tabs/context";
import {
  useSyncTempWorker,
  useUpdateTempWorker,
} from "core/temp_workers/hooks";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { TabsPartial } from "pages/main/candidates/partials/tabs.partial";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { flattenObject } from "utils";
import SyncModal from "./sync-modal";

type Props = {
  tempWorker: any;
  modalMode?: boolean;
};

type ExportStatusProps = {
  lastExport: any;
};

const ongoingExportStatuses = ['created', 'running', 'export_profile', 'export_files', 'export_qualifications', 'export_features'];

const ExportStatusModule = ({ lastExport }: ExportStatusProps) => {
  if (
    lastExport === null ||
    lastExport === undefined ||
    Object.keys(lastExport).length === 0
  )
    return <></>;
  if (lastExport.state === "running")
    return (
      <Typography
        variant="body2"
        color="secondary"
        sx={{ display: "flex", alignItems: "center" }}
      >
        <Sync sx={{ mr: 1 }} /> En cours de synchronisation
      </Typography>
    );

  return (
    <Typography variant="body2" color="#666D78">
      Mis à jour{lastExport.agent && ` par ${lastExport.agent}`} le{" "}
      {format(Date.parse(lastExport.created_at), "dd/MM/yy", { locale: fr })}
    </Typography>
  );
};

export const EditForm = ({ tempWorker, modalMode }: Props) => {
  // Form
  const methods = useForm({
    mode: "onChange",
    shouldUnregister: false,
    defaultValues: useMemo(() => tempWorker, [tempWorker]),
  });
  const { handleSubmit, formState, setError, reset, clearErrors } = methods;
  const { isDirty } = formState;

  // Modal
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Sync modal
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const openSyncModal = () => setSyncModalOpen(true);
  const handleSyncModalClose = () => setSyncModalOpen(false);
  const [exportId, setExportId] = useState<number | null>(null);

  // Mutations
  const mutation = useUpdateTempWorker({
    onError: (error) => {
      toast.error("Le candidat n'a pas pu être mis à jour.");

      if (error instanceof AxiosError) {
        const { full_messages, ...errors } = error.response?.data?.errors ?? {};
        Object.entries<Array<string>>(errors).forEach(([key, value]) => {
          if (!["base", "account", "address"].includes(key)) {
            setError(key, { type: "custom", message: value.join(", ") });
          }
        });
      }
    },
    onSuccess: (data: any) => {
      reset(data);
      toast.success("Le candidat a été mis à jour.");
    },
  });
  const syncMutation = useSyncTempWorker({
    onSuccess: (response) => {
      setExportId(response.export_id);
      openSyncModal();
    },
    onError: (error: any) => {
      toast.error("Le Candidat n'a pas pu être synchronisé.");

      if (error instanceof AxiosError) {
        const { full_messages, ...errors } = error.response?.data?.errors ?? {};
        Object.entries<Array<string>>(errors).forEach(([key, value]) => {
          if (!["base", "account", "address"].includes(key)) {
            setError(key, { type: "custom", message: value.join(", ") });
          }
        });
      }
    },
  });

  // Callbacks
  const onFormSubmit = (data: any) => {
    mutation.mutate({
      id: tempWorker.id,
      data: {
        ...data,
        international_bank_acount_number:
          data.international_bank_acount_number?.replace(/\s/g, ""),
        bank_identifier_code: data.bank_identifier_code?.replace(/\s/g, ""),
      },
    });
  };
  const onReset = useCallback(() => reset(tempWorker), [tempWorker, reset]);
  const onSyncClick = useCallback(
    () => syncMutation.mutate(tempWorker.id),
    [tempWorker, syncMutation]
  );

  // Effects
  useEffect(() => reset(tempWorker), [tempWorker, reset]);
  useEffect(() => {
    clearErrors();
    reset(tempWorker);
    if (tempWorker.changes !== undefined) {
      Object.entries(flattenObject(tempWorker.changes)).forEach(([k, v]) => {
        setError(k, { type: "anael", message: `valeur anael: ${v}` });
      });
    }
  }, [tempWorker, reset, clearErrors, setError]);

  // States
  const errors = useMemo(() => {
    if (Object.keys(formState.errors).length > 0)
      return {
        label: "Le formulaire n'est pas valide",
        title:
          "Veuillez corriger les erreurs du formulaire pour finaliser la création de cette fiche sur ANAEL :",
        description: Object.entries(formState.errors).map(
          ([field, error]) => `${field} ${error?.message}`
        ),
      };
    if (tempWorker.last_export?.state === "failed")
      return {
        label: "Synchronisation impossible",
        title: "Les élèments suivants empêchent la synchronisation du profil :",
        description: tempWorker.last_export.errors || [],
      };
    if (tempWorker.synchronized === false)
      return {
        label: "Données non correspondantes",
        title: "Certaine(s) valeur(s) diffère(nt) par rapport à ANAEL :",
        description: [],
      };

    return undefined;
  }, [formState, tempWorker]);
  const isInvalid = useMemo(
    () =>
      formState.isValid === false || Object.keys(formState.errors).length > 0,
    [formState]
  );
  const isLoading = useMemo(
    () => mutation.isLoading || syncMutation.isLoading,
    [mutation.isLoading, syncMutation.isLoading]
  );

  return (
    <Box component="main">
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          borderTopLeftRadius: "1rem",
          borderTopRightRadius: "1rem",
          ...modalMode ? {
            position: "sticky",
            top: 0,
            zIndex: 100,
          } : {}
        }}
      >
        <AvatarCandidate tempWorker={tempWorker} />
      </Box>
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit(onFormSubmit)} noValidate position="relative">
          <TabsContextProvider>
            <TabsPartial tempWorker={tempWorker} modalMode={modalMode} />
          </TabsContextProvider>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              borderTop: 1,
              py: "1.5rem",
              px: "1rem",
              borderColor: "divider",
              ...modalMode ? {
                backgroundColor: "background.paper",
                position: "sticky",
                bottom: 0,
                zIndex: 100,
              } : {}
            }}
          >
            <Box>
              {errors ? (
                <Link
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    fontWeight: "500",
                  }}
                  color="error"
                  onClick={handleOpen}
                >
                  <WarningAmberRounded sx={{ mr: "0.5rem" }} /> {errors.label}
                </Link>
              ) : (
                <ExportStatusModule lastExport={tempWorker.last_export} />
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "1rem",
              }}
            >
              <Button
                variant="contained"
                sx={{ backgroundColor: "text.secondary" }}
                onClick={onReset}
                disabled={isLoading || isDirty === false}
              >
                Annuler
              </Button>
              <Tooltip
                title={
                  isInvalid
                    ? "Veuillez corriger les erreurs."
                    : isDirty === false
                    ? "Aucun champ n'a été modifié."
                    : ""
                }
              >
                <span>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ color: "white" }}
                    disabled={isLoading || isDirty === false}
                    loading={mutation.isLoading}
                  >
                    Sauvegarder
                  </Button>
                </span>
              </Tooltip>
              {(exportId || ongoingExportStatuses.includes(tempWorker.last_export?.state)) ? (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    if (exportId === null) {
                      setExportId(tempWorker.last_export.id);
                    }
                    openSyncModal();
                  }}
                >
                  Synchronisation en cours
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onSyncClick}
                  disabled={isLoading || isDirty}
                  loading={syncMutation.isLoading}
                >
                  Synchroniser
                </Button>
              )}
            </Box>
          </Box>
        </Box>
          
      </FormProvider>
      {exportId && <SyncModal isOpen={syncModalOpen} onClose={handleSyncModalClose} exportId={exportId} />}
      {errors && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <Typography variant="h4">{errors.label}</Typography>
            <Typography variant="body2" sx={{ color: "#666D78" }}>
              {errors.title}
            </Typography>
            <List
              sx={{
                listStyleType: "disc",
                pl: 2,
                "& .MuiListItem-root": { display: "list-item" },
              }}
            >
              {errors.description.map((error: string, index: number) => (
                <ListItem key={index}>
                  <Typography>{error}</Typography>
                </ListItem>
              ))}
            </List>
            <Button
              onClick={handleClose}
              variant="contained"
              color="secondary"
              sx={{ color: "white" }}
            >
              Compris
            </Button>
          </Box>
        </Modal>
      )}
    </Box>
  );
};

const modalStyle = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 480,
  bgcolor: "background.paper",
  borderRadius: "1rem",
  boxShadow: 2,
  p: 4,
};
